/**
 * Ads Manager date presets: per-network reporting timezones and range math.
 * Reuses the preset computation logic from the dashboard presets module.
 */

import { formatCalendarDate, labelFromIsoRange } from "@/lib/dates"
import {
  computePresetRange as _computePresetRange,
  type PresetRange,
} from "@/pages/dashboard/lib/presets"

export const ROOTS_REPORTING_TIMEZONE = "America/New_York"
const KLOW_REPORTING_TIMEZONE = "America/Los_Angeles"

function nowInTimezoneDate(timeZone: string): Date {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date())

  const get = (type: "year" | "month" | "day") =>
    Number(parts.find((p) => p.type === type)?.value ?? "0")

  const year = get("year")
  const month = get("month")
  const day = get("day")
  return new Date(year, month - 1, day)
}

export type AdsManagerDatePreset =
  | "today"
  | "yesterday"
  | "thisweek"
  | "last7"
  | "last30"
  | "thismonth"
  | "custom"

export type PresetTimezoneMode = "local" | "reporting"

export type AdsManagerReportingTab = "roots" | "klow"

/** Per-network reporting timezones for Ads Manager date presets. */
export function reportingTimezoneForTab(
  tab: AdsManagerReportingTab | "adw" | "brm" | "unassigned",
): string | null {
  if (tab === "roots") return ROOTS_REPORTING_TIMEZONE
  if (tab === "klow") return KLOW_REPORTING_TIMEZONE
  return null
}

export function presetTimezoneModeForTab(
  tab: AdsManagerReportingTab | "adw" | "brm" | "unassigned",
): PresetTimezoneMode {
  return reportingTimezoneForTab(tab) ? "reporting" : "local"
}

export function computePresetRange(
  preset: AdsManagerDatePreset,
  timezoneMode: PresetTimezoneMode = "local",
  reportingTimeZone: string = ROOTS_REPORTING_TIMEZONE,
): PresetRange {
  const baseNow =
    timezoneMode === "reporting"
      ? nowInTimezoneDate(reportingTimeZone)
      : new Date()
  if (preset === "custom") {
    // Custom doesn't compute a range; caller provides dates.
    const today = _computePresetRange("today", baseNow)
    return { ...today, displayLabel: "Custom" }
  }
  // Rolling 7 calendar days including today (AM differs from dashboard
  // `last7`, which ends yesterday and skips incomplete today).
  if (preset === "last7") {
    const today = new Date(
      baseNow.getFullYear(),
      baseNow.getMonth(),
      baseNow.getDate(),
    )
    const start = new Date(today)
    start.setDate(today.getDate() - 6)
    const startDate = formatCalendarDate(start)
    const endDate = formatCalendarDate(today)
    return {
      startDate,
      endDate,
      displayLabel: labelFromIsoRange(startDate, endDate),
    }
  }
  // Map remaining presets to dashboard preset names (they match 1:1).
  return _computePresetRange(
    preset as
      | "today"
      | "yesterday"
      | "thisweek"
      | "last30"
      | "thismonth",
    baseNow,
  )
}

export function computePresetRangeForTab(
  preset: AdsManagerDatePreset,
  tab: AdsManagerReportingTab | "adw" | "brm" | "unassigned",
): PresetRange {
  const reportingTz = reportingTimezoneForTab(tab)
  return computePresetRange(
    preset,
    reportingTz ? "reporting" : "local",
    reportingTz ?? ROOTS_REPORTING_TIMEZONE,
  )
}
